import {
  getPortalRowByRowId,
  loginNeededToHandleZeroReportsForRowId,
  LOGIN_ROW_OPTINS,
  LOGIN_ROW_SLIP_PLUS,
  MAP_UNIQUE_LICENSE_JURISDICTION_CODES,
  OPTINS_COVERED_STATES,
  SLIP_PLUS_COVERED_STATES,
  STATE_LOGIN_PORTAL_ROWS,
} from './stateLoginPortalMap'
import {
  licenseDisplayName,
  type IllinoisLoginMode,
  type LicenseRow,
  type StateLoginRow,
} from '../types/onboarding'
import {
  mergeLoginClientCompanyName,
} from '../wizard/licenseSteps/businessEntityNames'
import { loginRowCompleteForPrimaryStep } from '../wizard/licenseSteps/loginRowComplete'

/** @deprecated Use `MAP_UNIQUE_LICENSE_JURISDICTION_CODES` from `stateLoginPortalMap.ts`. */
export const STATE_CODES_WITH_PORTALS: readonly string[] = [...MAP_UNIQUE_LICENSE_JURISDICTION_CODES]

export { LOGIN_ROW_OPTINS, LOGIN_ROW_SLIP_PLUS, OPTINS_COVERED_STATES, SLIP_PLUS_COVERED_STATES }

/** User-added SL Logins rows (not from the workbook); stable `stateCode` id prefix. */
export const CUSTOM_STATE_LOGIN_PREFIX = 'LOGIN-CUSTOM-' as const

export const ILLINOIS_PORTAL_ROW_ID = 'IL' as const
export const ILLINOIS_IND_PREFIX = 'IL-IND-' as const

export function isCustomStateLoginRowId(stateCode: string): boolean {
  return stateCode.startsWith(CUSTOM_STATE_LOGIN_PREFIX)
}

export function isIllinoisIndividualLoginRowId(stateCode: string): boolean {
  return stateCode.startsWith(ILLINOIS_IND_PREFIX)
}

export function illinoisIndividualLoginRowId(licenseeId: string): string {
  return `${ILLINOIS_IND_PREFIX}${licenseeId}`
}

/** Individual Licenses with Resident SL License State = IL. */
export function illinoisIndividualsForLogins(licenses: LicenseRow[]): LicenseRow[] {
  return licenses.filter(
    (l) => l.entity === 'Individual' && l.licenseState.toUpperCase() === 'IL',
  )
}

export function isIllinoisLoginRow(row: StateLoginRow): boolean {
  return (
    row.stateCode === ILLINOIS_PORTAL_ROW_ID ||
    isIllinoisIndividualLoginRowId(row.stateCode) ||
    row.licenseJurisdictionCode?.toUpperCase() === 'IL'
  )
}

export function illinoisLoginModeBlocksContinue(
  activeBusinessStates: readonly string[],
  mode: IllinoisLoginMode,
): boolean {
  const writingIl = activeBusinessStates.some((c) => c.toUpperCase() === 'IL')
  return writingIl && mode === 'unset'
}

function isDashPlaceholderLicensee(value: string): boolean {
  const t = value.trim()
  return t === '—' || t === '--' || t === '–'
}

/** Default Licensee column text from license rows (two-letter jurisdictions only; platforms stay empty until set). */
export function suggestLicenseeDisplay(
  licenses: LicenseRow[],
  licenseJurisdictionCode: string | null,
): string {
  if (!licenseJurisdictionCode || licenseJurisdictionCode.length !== 2) return ''
  const inState = licenses.filter((l) => l.licenseState === licenseJurisdictionCode)
  const drlp = inState.find((l) => l.isDrlp)
  if (drlp) return licenseDisplayName(drlp)
  if (inState[0]) return licenseDisplayName(inState[0])
  return ''
}

function mergeLicenseeDisplay(
  existing: string | undefined | null,
  licenses: LicenseRow[],
  licenseJurisdictionCode: string | null,
): string {
  const suggest = suggestLicenseeDisplay(licenses, licenseJurisdictionCode)
  if (existing == null) return suggest
  if (isDashPlaceholderLicensee(existing)) return suggest
  return existing.trim()
}

function finalizeLoginRow(row: StateLoginRow, licenses: LicenseRow[]): StateLoginRow {
  return { ...row, complete: loginRowCompleteForPrimaryStep(row, licenses) }
}

function mergeOrCreatePortalLoginRow(
  mapRow: (typeof STATE_LOGIN_PORTAL_ROWS)[number],
  previous: StateLoginRow[],
  prevByRowId: Map<string, StateLoginRow>,
  licenses: LicenseRow[],
  extras: Partial<StateLoginRow> = {},
): StateLoginRow {
  const existing = prevByRowId.get(mapRow.rowId)
  if (existing) {
    const others = previous
      .filter((p) => p.stateCode !== mapRow.rowId)
      .map((p) => p.clientCompanyName)
    const clientCompanyName = mergeLoginClientCompanyName(
      existing.clientCompanyName ?? '',
      licenses,
      others,
    )
    return finalizeLoginRow(
      {
        ...existing,
        portalUrl: mapRow.portalUrl,
        licenseJurisdictionCode: mapRow.licenseJurisdictionCode,
        licenseeDisplay: mergeLicenseeDisplay(
          existing.licenseeDisplay,
          licenses,
          mapRow.licenseJurisdictionCode,
        ),
        pendingLoginWizard: existing.pendingLoginWizard ?? false,
        clientCompanyName,
        illinoisSourceLicenseId: undefined,
        illinoisPendingChoice: false,
        ...extras,
      },
      licenses,
    )
  }
  const others = previous.map((p) => p.clientCompanyName)
  const clientCompanyName = mergeLoginClientCompanyName(undefined, licenses, others)
  return finalizeLoginRow(
    {
      stateCode: mapRow.rowId,
      licenseJurisdictionCode: mapRow.licenseJurisdictionCode,
      portalUrl: mapRow.portalUrl,
      miscManaged: false,
      username: '',
      password: '',
      complete: false,
      licenseeDisplay: suggestLicenseeDisplay(licenses, mapRow.licenseJurisdictionCode),
      pendingLoginWizard: false,
      clientCompanyName,
      illinoisSourceLicenseId: undefined,
      illinoisPendingChoice: false,
      ...extras,
    },
    licenses,
  )
}

function buildIllinoisIndividualRows(
  ilMapRow: (typeof STATE_LOGIN_PORTAL_ROWS)[number],
  previous: StateLoginRow[],
  prevByRowId: Map<string, StateLoginRow>,
  licenses: LicenseRow[],
): StateLoginRow[] {
  const individuals = illinoisIndividualsForLogins(licenses)
  return individuals.map((license) => {
    const rowId = illinoisIndividualLoginRowId(license.licenseeId)
    const existing = prevByRowId.get(rowId)
    const displayName = licenseDisplayName(license)
    if (existing) {
      const others = previous
        .filter((p) => p.stateCode !== rowId)
        .map((p) => p.clientCompanyName)
      const clientCompanyName = mergeLoginClientCompanyName(
        existing.clientCompanyName ?? '',
        licenses,
        others,
      )
      return finalizeLoginRow(
        {
          ...existing,
          portalUrl: ilMapRow.portalUrl,
          licenseJurisdictionCode: 'IL',
          licenseeDisplay:
            existing.licenseeDisplay.trim().length > 0 &&
            !isDashPlaceholderLicensee(existing.licenseeDisplay)
              ? existing.licenseeDisplay
              : displayName,
          pendingLoginWizard: existing.pendingLoginWizard ?? false,
          clientCompanyName,
          illinoisSourceLicenseId: license.licenseeId,
          illinoisPendingChoice: false,
        },
        licenses,
      )
    }
    const others = previous.map((p) => p.clientCompanyName)
    const clientCompanyName = mergeLoginClientCompanyName(
      license.companyName,
      licenses,
      others,
    )
    return finalizeLoginRow(
      {
        stateCode: rowId,
        licenseJurisdictionCode: 'IL',
        portalUrl: ilMapRow.portalUrl,
        miscManaged: false,
        username: '',
        password: '',
        complete: false,
        licenseeDisplay: displayName,
        pendingLoginWizard: false,
        clientCompanyName,
        illinoisSourceLicenseId: license.licenseeId,
        illinoisPendingChoice: false,
      },
      licenses,
    )
  })
}

/**
 * Builds the login table from General Questions (`miscHandledStates` +
 * `activeBusinessStates`) and the onboarding workbook rows in
 * `STATE_LOGIN_PORTAL_ROWS`. Preserves credentials for rows that still exist.
 *
 * A state-specific row is required when either:
 * - the client writes business in that state, or
 * - workbook column B says a login is still needed to handle zero reports.
 *
 * OPTins / SLIP+ appear whenever at least one MISC-handled state is selected so
 * the table is never empty solely because those states have no dedicated portal row
 * in the workbook (e.g. Alabama is MISC-handled but not mapped to a state portal).
 *
 * When IL is an active business state, rows fan out by `illinoisLoginMode`
 * (chooser / per-individual / corporate).
 */
export function reconcileStateLogins(
  selectedMiscStates: string[],
  activeBusinessStates: string[],
  previous: StateLoginRow[],
  licenses: LicenseRow[],
  illinoisLoginMode: IllinoisLoginMode = 'unset',
): StateLoginRow[] {
  const selectedUpper = new Set(selectedMiscStates.map((c) => c.toUpperCase()))
  const activeUpper = new Set(activeBusinessStates.map((c) => c.toUpperCase()))
  const allowedJurisdiction = new Set(MAP_UNIQUE_LICENSE_JURISDICTION_CODES.map((c) => c.toUpperCase()))
  const prevByRowId = new Map(previous.map((r) => [r.stateCode, r]))
  const writingIllinois = activeUpper.has('IL')

  const requiredRowIds = new Set<string>()

  for (const code of Array.from(selectedUpper)) {
    if (!allowedJurisdiction.has(code)) continue
    for (const row of STATE_LOGIN_PORTAL_ROWS) {
      if (row.licenseJurisdictionCode?.toUpperCase() === code) {
        const loginNeededForZeroReports = loginNeededToHandleZeroReportsForRowId(row.rowId)
        if (activeUpper.has(code) || loginNeededForZeroReports) {
          requiredRowIds.add(row.rowId)
        }
      }
    }
  }

  if (selectedUpper.size > 0) {
    requiredRowIds.add(LOGIN_ROW_OPTINS)
    requiredRowIds.add(LOGIN_ROW_SLIP_PLUS)
  }

  const orderedRows = STATE_LOGIN_PORTAL_ROWS.filter((r) => requiredRowIds.has(r.rowId))
  const built: StateLoginRow[] = []

  for (const mapRow of orderedRows) {
    if (mapRow.rowId === ILLINOIS_PORTAL_ROW_ID && writingIllinois) {
      if (illinoisLoginMode === 'individual') {
        const individualRows = buildIllinoisIndividualRows(
          mapRow,
          previous,
          prevByRowId,
          licenses,
        )
        if (individualRows.length === 0) {
          built.push(
            mergeOrCreatePortalLoginRow(mapRow, previous, prevByRowId, licenses, {
              illinoisPendingChoice: true,
              pendingLoginWizard: false,
              username: '',
              password: '',
            }),
          )
        } else {
          built.push(...individualRows)
        }
        continue
      }
      if (illinoisLoginMode === 'corporate') {
        built.push(mergeOrCreatePortalLoginRow(mapRow, previous, prevByRowId, licenses))
        continue
      }
      built.push(
        mergeOrCreatePortalLoginRow(mapRow, previous, prevByRowId, licenses, {
          illinoisPendingChoice: true,
          pendingLoginWizard: false,
          username: '',
          password: '',
        }),
      )
      continue
    }

    built.push(mergeOrCreatePortalLoginRow(mapRow, previous, prevByRowId, licenses))
  }

  const preservedCustom = previous.filter((r) => isCustomStateLoginRowId(r.stateCode))
  return remapLoginClientsAfterLicenseChange([...built, ...preservedCustom], licenses)
}

/** Append a user-defined login row (e.g. from **+ Add** on SL Logins). */
export function createCustomStateLoginRow(
  licenses: LicenseRow[],
  existingLogins: StateLoginRow[],
): StateLoginRow {
  const others = existingLogins.map((p) => p.clientCompanyName)
  const clientCompanyName = mergeLoginClientCompanyName(undefined, licenses, others)
  const row: StateLoginRow = {
    stateCode: `${CUSTOM_STATE_LOGIN_PREFIX}${crypto.randomUUID()}`,
    licenseJurisdictionCode: null,
    portalUrl: '',
    miscManaged: false,
    username: '',
    password: '',
    complete: false,
    licenseeDisplay: '',
    pendingLoginWizard: false,
    clientCompanyName,
  }
  return { ...row, complete: loginRowCompleteForPrimaryStep(row, licenses) }
}

/** After Business / Individual license edits, re-merge Client selections and row completion. */
export function remapLoginClientsAfterLicenseChange(
  rows: StateLoginRow[],
  licenses: LicenseRow[],
): StateLoginRow[] {
  return rows.map((r) => {
    const others = rows.filter((x) => x.stateCode !== r.stateCode).map((x) => x.clientCompanyName)
    const clientCompanyName = mergeLoginClientCompanyName(r.clientCompanyName ?? '', licenses, others)
    const next = { ...r, clientCompanyName }
    return { ...next, complete: loginRowCompleteForPrimaryStep(next, licenses) }
  })
}

/** Display string for the State column (uses workbook labels, including duplicate geographies). */
export function loginStateColumnLabel(rowId: string): string {
  if (isCustomStateLoginRowId(rowId)) {
    const idPart = rowId.slice(CUSTOM_STATE_LOGIN_PREFIX.length).replace(/-/g, '')
    const short = (idPart.slice(0, 6) || 'NEW').toUpperCase()
    return `Other (${short})`
  }
  if (isIllinoisIndividualLoginRowId(rowId)) {
    return getPortalRowByRowId(ILLINOIS_PORTAL_ROW_ID)?.stateColumnLabel ?? 'Illinois'
  }
  const row = getPortalRowByRowId(rowId)
  return row?.stateColumnLabel ?? rowId
}
